import React, { useState, useEffect } from 'react';
import { Channel, CreateChannelRequest, UpdateChannelRequest } from '../types';
import { useUnsavedChanges } from '../../../../hooks/useUnsavedChanges';
import { useDirtyChangeReport } from '../../../../hooks/useDirtyChangeReport';
import '../../../../components/common/CRMForm.css';

interface AiChannelSettingsFormProps {
  channel: Channel | null;
  onSave: (data: CreateChannelRequest | UpdateChannelRequest) => Promise<void>;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
}

const AiChannelSettingsForm: React.FC<AiChannelSettingsFormProps> = ({ channel, onSave, onCancel, onDirtyChange }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  const { confirmNavigation } = useUnsavedChanges({ hasChanges });
  useDirtyChangeReport(hasChanges, onDirtyChange);
  const isEditing = Boolean(channel?.id);

  useEffect(() => {
    if (channel) {
      setName(channel.name);
      setHasChanges(false);
    } else {
      setName('');
      setHasChanges(false);
    }
  }, [channel]);

  const markChanged = () => setHasChanges(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!name.trim()) nextErrors.name = 'Channel name is required';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSaving(true);
    try {
      if (isEditing && channel) {
        await onSave({ id: channel.id, name: name.trim(), type: 'ai' });
      } else {
        await onSave({ name: name.trim(), type: 'ai' });
      }
      setHasChanges(false);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!confirmNavigation()) return;
    onCancel();
  };

  return (
    <div className="channel-settings-form">
      <h2>{isEditing ? 'AI Channel Settings' : 'Add AI Channel'}</h2>
      <p className="channel-settings-description">
        Smart inboxes organize messages from your other channels into Focus, News, and Other.
        Additional settings will be available here later.
      </p>

      <form onSubmit={handleSubmit} className="app-form">
        <div className="app-form-group">
          <label className="app-form-label" htmlFor="ai-channel-name">Channel Name</label>
          <input
            id="ai-channel-name"
            className={`app-form-input${errors.name ? ' app-form-input-error' : ''}`}
            type="text"
            value={name}
            onChange={e => { setName(e.target.value); markChanged(); }}
            placeholder="AI Inbox"
            maxLength={200}
          />
          {errors.name && <span className="app-form-error">{errors.name}</span>}
        </div>

        <div className="form-actions">
          <button type="button" className="app-btn app-btn-secondary" onClick={handleCancel} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="app-btn app-btn-primary" disabled={saving}>
            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Channel'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AiChannelSettingsForm;
